// Track 1 — Decoded JWT user state.
//
// Reads the auth token from localStorage, decodes it, and returns a
// stable identity (id, email, role) plus auth status. Re-evaluates on
// 'storage' events so a login/logout in another tab is reflected.
//
// JWT payload shape (per AuthService.login):
//   { email: string, sub: number, role: string, iat: number, exp: number }

import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

export interface CurrentUser {
  id: number | null;
  email: string | null;
  role: string | null;
  // Wave 20.18 — set when a superadmin used /admin to impersonate
  // this user. Original superadmin id; ImpersonationBanner uses it
  // to swap back and to render "Impersonating X …".
  impersonatedBy: number | null;
  isAuthenticated: boolean;
  isExpired: boolean;
}

interface JwtPayload {
  sub?: number;
  email?: string;
  role?: string;
  impersonatedBy?: number;
  exp?: number;
}

const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

function readUser(): CurrentUser {
  const empty: CurrentUser = {
    id: null,
    email: null,
    role: null,
    impersonatedBy: null,
    isAuthenticated: false,
    isExpired: false,
  };
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return empty;
    const decoded = jwtDecode<JwtPayload>(token);
    const nowSec = Math.floor(Date.now() / 1000);
    const expired = decoded.exp != null && decoded.exp <= nowSec;
    return {
      id: decoded.sub ?? null,
      email: decoded.email ?? null,
      // Default role 'general' so older tokens issued before Track 1
      // (which didn't include role) still flow through guards cleanly.
      role: decoded.role ?? 'general',
      impersonatedBy: decoded.impersonatedBy ?? null,
      isAuthenticated: !expired,
      isExpired: expired,
    };
  } catch {
    return empty;
  }
}

export function useCurrentUser(): CurrentUser {
  const [user, setUser] = useState<CurrentUser>(() => readUser());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === null) setUser(readUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return user;
}

/** Synchronous lookup for code outside a React component (e.g. one-off
 *  fetch helpers). Doesn't subscribe to changes. */
export function readCurrentUser(): CurrentUser {
  return readUser();
}
